export interface ParsedCssColor {
  red: number;
  green: number;
  blue: number;
  alpha: number;
}

const BLACK = '#000000';
const WHITE: ParsedCssColor = { red: 255, green: 255, blue: 255, alpha: 1 };

const parseRgbChannel = (value: string): number | null => {
  if (!value.trim()) {
    return null;
  }
  const parsed = value.endsWith('%') ? (Number(value.slice(0, -1)) / 100) * 255 : Number(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 255 ? parsed : null;
};

const parseAlphaChannel = (value: string): number | null => {
  if (!value.trim()) {
    return null;
  }
  const parsed = value.endsWith('%') ? Number(value.slice(0, -1)) / 100 : Number(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : null;
};

const createColor = (channels: string[], alpha = '1'): ParsedCssColor | null => {
  if (channels.length !== 3) {
    return null;
  }

  const [red, green, blue] = channels.map(parseRgbChannel);
  const parsedAlpha = parseAlphaChannel(alpha);
  if (red === null || green === null || blue === null || parsedAlpha === null) {
    return null;
  }

  return { red, green, blue, alpha: parsedAlpha };
};

const parseHexColor = (value: string): ParsedCssColor | null => {
  const match = value.match(/^#([\da-f]{3}|[\da-f]{4}|[\da-f]{6}|[\da-f]{8})$/i);
  if (!match) {
    return null;
  }

  const hex = match[1];
  const expanded = hex.length <= 4 ? [...hex].map(character => character.repeat(2)).join('') : hex;
  const hasAlpha = expanded.length === 8;

  return {
    red: Number.parseInt(expanded.slice(0, 2), 16),
    green: Number.parseInt(expanded.slice(2, 4), 16),
    blue: Number.parseInt(expanded.slice(4, 6), 16),
    alpha: hasAlpha ? Number.parseInt(expanded.slice(6, 8), 16) / 255 : 1
  };
};

const parseFunctionalColor = (value: string): ParsedCssColor | null => {
  const match = value.match(/^rgba?\((.*)\)$/i);
  if (!match) {
    return null;
  }

  const content = match[1].trim();
  if (content.includes(',')) {
    const parts = content.split(',').map(part => part.trim());
    if (parts.length < 3 || parts.length > 4) {
      return null;
    }

    return createColor(parts.slice(0, 3), parts[3] ?? '1');
  }

  const slashParts = content.split('/').map(part => part.trim());
  if (slashParts.length > 2) {
    return null;
  }

  const [channelText, alphaText] = slashParts;
  return createColor(channelText.split(/\s+/), alphaText ?? '1');
};

const parseChannelToken = (value: string): ParsedCssColor | null => {
  if (!value.includes(',') && !/\s/.test(value)) {
    return null;
  }
  const slashParts = value.split('/').map(part => part.trim());
  if (slashParts.length > 2) {
    return null;
  }

  const [channelText, alphaText] = slashParts;
  return createColor(channelText.split(/[\s,]+/).filter(Boolean), alphaText ?? '1');
};

export const parseCssColor = (color: string): ParsedCssColor | null => {
  const value = color?.trim().toLowerCase();
  if (!value) {
    return null;
  }
  if (value === 'transparent') {
    return { red: 0, green: 0, blue: 0, alpha: 0 };
  }

  return parseHexColor(value) || parseFunctionalColor(value) || parseChannelToken(value);
};

const composite = (foreground: ParsedCssColor, background: ParsedCssColor): ParsedCssColor => {
  const alpha = foreground.alpha + background.alpha * (1 - foreground.alpha);
  if (alpha === 0) {
    return { red: 0, green: 0, blue: 0, alpha: 0 };
  }

  return {
    red: (foreground.red * foreground.alpha + background.red * background.alpha * (1 - foreground.alpha)) / alpha,
    green: (foreground.green * foreground.alpha + background.green * background.alpha * (1 - foreground.alpha)) / alpha,
    blue: (foreground.blue * foreground.alpha + background.blue * background.alpha * (1 - foreground.alpha)) / alpha,
    alpha
  };
};

const quantize = (color: ParsedCssColor): ParsedCssColor => ({
  red: Math.round(color.red),
  green: Math.round(color.green),
  blue: Math.round(color.blue),
  alpha: color.alpha
});

const relativeLuminance = (color: ParsedCssColor): number => {
  const linearChannels = [color.red, color.green, color.blue].map(channel => {
    const normalized = channel / 255;
    return normalized <= 0.04045 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
  });

  return linearChannels[0] * 0.2126 + linearChannels[1] * 0.7152 + linearChannels[2] * 0.0722;
};

const contrastBetween = (foreground: ParsedCssColor, background: ParsedCssColor): number => {
  const opaqueBackground = composite(quantize(background), WHITE);
  const opaqueForeground = composite(quantize(foreground), opaqueBackground);
  const foregroundLuminance = relativeLuminance(opaqueForeground);
  const backgroundLuminance = relativeLuminance(opaqueBackground);
  const lighter = Math.max(foregroundLuminance, backgroundLuminance);
  const darker = Math.min(foregroundLuminance, backgroundLuminance);

  return (lighter + 0.05) / (darker + 0.05);
};

export const flattenCssColor = (color: string, surface: string): string => {
  const parsedSurface = parseCssColor(surface);
  if (!parsedSurface || parsedSurface.alpha < 1) {
    return BLACK;
  }

  const opaqueSurface = quantize(parsedSurface);
  const parsedColor = quantize(parseCssColor(color) || opaqueSurface);
  const flattened = quantize(composite(parsedColor, opaqueSurface));

  return `rgb(${flattened.red} ${flattened.green} ${flattened.blue})`;
};

export const getContrastRatio = (foreground: string, background: string): number => {
  const parsedForeground = parseCssColor(foreground);
  const parsedBackground = parseCssColor(background);
  if (!parsedForeground || !parsedBackground) {
    return 1;
  }

  return contrastBetween(parsedForeground, parsedBackground);
};

export const getAccessibleForeground = (
  background: string,
  candidates: readonly string[],
  minimumRatio: number
): string => {
  const parsedBackground = parseCssColor(background);
  const validCandidates = candidates
    .map(candidate => ({ candidate, color: parseCssColor(candidate) }))
    .filter((entry): entry is { candidate: string; color: ParsedCssColor } => entry.color !== null);

  if (!validCandidates.length) {
    return BLACK;
  }
  if (!parsedBackground) {
    return validCandidates[0].candidate;
  }

  let bestCandidate = validCandidates[0].candidate;
  let bestRatio = -1;
  for (const { candidate, color } of validCandidates) {
    const ratio = contrastBetween(color, parsedBackground);
    if (ratio >= minimumRatio) {
      return candidate;
    }
    if (ratio > bestRatio) {
      bestCandidate = candidate;
      bestRatio = ratio;
    }
  }

  return bestCandidate;
};

export const toRgbChannels = (color: string, fallback = BLACK): string => {
  const parsed = parseCssColor(color) || parseCssColor(fallback) || parseCssColor(BLACK)!;
  const channels = `${Math.round(parsed.red)} ${Math.round(parsed.green)} ${Math.round(parsed.blue)}`;
  return parsed.alpha < 1 ? `${channels} / ${parsed.alpha}` : channels;
};
